import React, { useEffect, useState } from 'react';
import { Download, Film, RefreshCw, Calendar, Globe, User } from 'lucide-react';

/**
 * Admin-only panel: shows weekly training-bundle downloads and a button to
 * re-capture the video walkthroughs. Rendered as an inline card at the top of
 * the admin Help & Training tab.
 */
export default function AdminTrainingAnalytics() {
  const [stats, setStats] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  const authHeaders = token
    ? { Authorization: `Bearer ${token}` }
    : {};

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [aRes, sRes] = await Promise.all([
          fetch(`${BACKEND}/api/admin/training/bundle-analytics?days=30`,
                { credentials: 'include', headers: authHeaders }),
          fetch(`${BACKEND}/api/admin/training/refresh-status`,
                { credentials: 'include', headers: authHeaders }),
        ]);
        if (aRes.ok && !cancelled) setStats(await aRes.json());
        if (sRes.ok && !cancelled) setRefreshStatus(await sRes.json());
      } catch (_) { /* silent — admin-only */ }
      finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
     
  }, [BACKEND]);

  const triggerRefresh = async () => {
    setRefreshing(true);
    try {
      await fetch(`${BACKEND}/api/admin/training/refresh-videos`,
                  { method: 'POST', credentials: 'include', headers: authHeaders });
      setTimeout(async () => {
        try {
          const sRes = await fetch(`${BACKEND}/api/admin/training/refresh-status`,
                                   { credentials: 'include', headers: authHeaders });
          if (sRes.ok) setRefreshStatus(await sRes.json());
        } catch (_) { /* silent */ }
        setRefreshing(false);
      }, 5000);
    } catch (_) {
      setRefreshing(false);
    }
  };

  if (loading || !stats) return null;

  const weeks = stats.by_week || [];
  const maxCount = Math.max(1, ...weeks.map((w) => w.count));

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm mb-8"
      data-testid="admin-training-analytics"
    >
      <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Download className="w-5 h-5 text-emerald-600" />
            <h2 className="text-xl font-bold text-slate-900">Bundle Analytics</h2>
            <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-900 text-white px-2 py-0.5 rounded-full">
              Admin
            </span>
          </div>
          <p className="text-sm text-slate-600">
            Last 30 days · {stats.total_downloads} download{stats.total_downloads === 1 ? '' : 's'}
            {stats.total_bytes ? ` · ${(stats.total_bytes / 1024 / 1024).toFixed(1)} MB served` : ''}
          </p>
        </div>
        <button
          onClick={triggerRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 text-white text-xs font-semibold transition"
          data-testid="refresh-videos-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing…' : 'Refresh videos'}
        </button>
      </div>

      {/* Weekly bar chart */}
      <div className="mb-6">
        <div className="flex items-center gap-1 text-xs font-semibold text-slate-500 mb-2">
          <Calendar className="w-3.5 h-3.5" />
          Downloads by ISO week
        </div>
        {weeks.length === 0 && (
          <p className="text-xs text-slate-400 italic">No downloads yet — the ZIP button hasn&apos;t been used in the last 30 days.</p>
        )}
        <div className="flex items-end gap-2 h-32">
          {weeks.slice(0, 12).reverse().map((w) => (
            <div key={w.week} className="flex-1 flex flex-col items-center gap-1 min-w-0">
              <span className="text-[10px] text-slate-500 font-mono">{w.count}</span>
              <div
                className="w-full bg-gradient-to-t from-emerald-500 to-teal-400 rounded-t"
                style={{ height: `${(w.count / maxCount) * 100}%`, minHeight: '2px' }}
                title={`${w.week}: ${w.count} downloads`}
              />
              <span className="text-[9px] text-slate-400 truncate w-full text-center">{w.week}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* By language */}
        <div className="rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-1 text-xs font-semibold text-slate-500 mb-2">
            <Globe className="w-3.5 h-3.5" />
            By language
          </div>
          {Object.entries(stats.by_lang || {}).length === 0 && (
            <p className="text-xs text-slate-400 italic">No data yet.</p>
          )}
          {Object.entries(stats.by_lang || {}).sort((a, b) => b[1] - a[1]).map(([code, n]) => (
            <div key={code} className="flex items-center justify-between text-sm py-1">
              <span className="font-mono uppercase text-slate-700">{code}</span>
              <span className="font-semibold text-slate-900">{n}</span>
            </div>
          ))}
        </div>
        {/* By role */}
        <div className="rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-1 text-xs font-semibold text-slate-500 mb-2">
            <User className="w-3.5 h-3.5" />
            By role pack
          </div>
          {Object.entries(stats.by_role || {}).length === 0 && (
            <p className="text-xs text-slate-400 italic">No data yet.</p>
          )}
          {Object.entries(stats.by_role || {}).sort((a, b) => b[1] - a[1]).map(([r, n]) => (
            <div key={r} className="flex items-center justify-between text-sm py-1">
              <span className="capitalize text-slate-700">{r === 'all' ? 'Full pack' : r}</span>
              <span className="font-semibold text-slate-900">{n}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Refresh status */}
      {refreshStatus && (
        <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs">
          <div className="flex items-center gap-1 font-semibold text-slate-600 mb-1">
            <Film className="w-3.5 h-3.5" />
            Video refresh status
          </div>
          <div className="grid sm:grid-cols-2 gap-1 text-slate-600">
            <div>MP4 count: <b className="text-slate-900">{refreshStatus.mp4_count}</b></div>
            <div>
              Latest MP4:{' '}
              <b className="text-slate-900">
                {refreshStatus.latest_mp4_at
                  ? new Date(refreshStatus.latest_mp4_at).toLocaleString()
                  : '—'}
              </b>
            </div>
            {refreshStatus.last_refresh?.at && (
              <div className="col-span-full">
                Last triggered: <b className="text-slate-900">{new Date(refreshStatus.last_refresh.at).toLocaleString()}</b>
              </div>
            )}
          </div>
          {refreshStatus.log_tail && (
            <details className="mt-2">
              <summary className="cursor-pointer text-slate-500 hover:text-slate-900">Show recent log</summary>
              <pre className="mt-1 p-2 rounded bg-slate-900 text-slate-100 text-[10px] font-mono overflow-x-auto max-h-40">
                {refreshStatus.log_tail}
              </pre>
            </details>
          )}
        </div>
      )}
    </section>
  );
}
